import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Target, Search, Mail, Lock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LockedModule } from "./SchoolAnalytics";
import { provinces } from "@/data/turkeyData";

export default function SchoolTargeting() {
  const [modules, setModules] = useState([]);
  const [filters, setFilters] = useState({ grade_level: "", province: "", min_score: "", exam_type: "" });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [withContact, setWithContact] = useState(false);
  const mod = modules.find((m) => m.key === "school_targeting");
  const owned = mod?.owned;
  const leadsMod = modules.find((m) => m.key === "student_leads");
  const leadsOwned = leadsMod?.owned;

  useEffect(() => { api.get(`${V1}/school-modules`).then((r) => setModules(r.data || [])); }, []);

  const search = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filters.grade_level) params.set("grade_level", filters.grade_level);
      if (filters.province) params.set("province", filters.province);
      if (filters.min_score) params.set("min_score", filters.min_score);
      if (filters.exam_type) params.set("exam_type", filters.exam_type);
      if (withContact && leadsOwned) params.set("with_contact", "true");
      const { data } = await api.get(`${V1}/school/targeting/students?${params.toString()}`);
      setItems(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
    finally { setBusy(false); }
  };

  const invite = async (child_id, parent_id) => {
    try {
      await api.post(`${V1}/invitations`, {
        child_id, parent_id, scholarship_rate: 25, scholarship_type: "achievement",
        message: "Sizi okulumuza davet etmek istiyoruz.",
      });
      toast.success("Davet gönderildi");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gönderilemedi"); }
  };

  if (!mod) return <div className="text-muted-foreground">Yükleniyor…</div>;
  if (!owned) return <LockedModule mod={mod} testid="targeting-locked" />;

  const showingContact = withContact && leadsOwned;

  return (
    <div className="space-y-6" data-testid="school-targeting-page">
      <div>
        <div className="overline text-accent">Modül · {mod.label}</div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Target className="h-7 w-7 text-primary" /> Öğrenci Hedefleme</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Anonimleştirilmiş öğrenci profillerini filtreleyin ve okulunuza davet edin.
          KVKK gereği isim ve iletişim bilgileri gizlidir; davet aracılığıyla veliye ulaşılır.
        </p>
      </div>

      <div className="p-4 rounded-xl border border-accent/30 bg-accent/5 flex items-center justify-between gap-4 flex-wrap" data-testid="student-leads-upsell">
        <div className="flex items-center gap-2 text-sm">
          {leadsOwned ? <ShieldCheck className="h-4 w-4 text-success" /> : <Lock className="h-4 w-4 text-accent" />}
          <div>
            <div className="font-medium">Öğrenci İletişim Listesi Paketi</div>
            <div className="text-xs text-muted-foreground">
              Sadece iletişim bilgisini paylaşmaya <b>açıkça izin vermiş (opt-in)</b> veli/öğrencilerin
              ad-soyad ve iletişim bilgilerini görün. İzin vermeyenler her zaman maskeli kalır.
            </div>
          </div>
        </div>
        {leadsOwned ? (
          <label className="flex items-center gap-2 text-sm shrink-0" data-testid="with-contact-toggle">
            <Switch checked={withContact} onCheckedChange={setWithContact} />
            <span>İletişimli Göster</span>
          </label>
        ) : (
          <span className="text-xs text-muted-foreground shrink-0">Bu paket &quot;Modüllerim&quot; sayfasından satın alınabilir.</span>
        )}
      </div>

      <div className="p-4 rounded-xl border border-border bg-card grid md:grid-cols-5 gap-3 items-end">
        <div><Label>Sınıf</Label><Input value={filters.grade_level} onChange={(e) => setFilters({ ...filters, grade_level: e.target.value })} placeholder="8" data-testid="tgt-grade" /></div>
        <div>
          <Label>İl</Label>
          <Select value={filters.province} onValueChange={(v) => setFilters({ ...filters, province: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="tgt-province"><SelectValue placeholder="Tümü" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Sınav Tipi</Label><Input value={filters.exam_type} onChange={(e) => setFilters({ ...filters, exam_type: e.target.value })} placeholder="LGS" data-testid="tgt-examtype" /></div>
        <div><Label>Min Ortalama (%)</Label><Input type="number" value={filters.min_score} onChange={(e) => setFilters({ ...filters, min_score: e.target.value })} placeholder="70" data-testid="tgt-minscore" /></div>
        <Button onClick={search} disabled={busy} data-testid="tgt-search-btn"><Search className="h-4 w-4" /> Ara</Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-3">Öğrenci</th>
              {showingContact && <th className="text-left p-3">Veli / İletişim</th>}
              <th className="text-left p-3">Sınıf</th>
              <th className="text-left p-3">Şehir</th>
              <th className="text-left p-3">Sınav</th>
              <th className="text-left p-3">Ortalama</th>
              <th className="text-left p-3">Deneme</th>
              <th className="text-left p-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={showingContact ? 8 : 7} className="p-8 text-center text-muted-foreground">Sonuç yok. Filtreleyip arayın.</td></tr>}
            {items.map((s) => (
              <tr key={s.child_id} className="border-t border-border" data-testid={`tgt-row-${s.child_id}`}>
                <td className="p-3 font-mono text-xs">{showingContact ? (s.full_name || s.first_letter) : s.first_letter}</td>
                {showingContact && (
                  <td className="p-3 text-xs">
                    <div>{s.parent_name || "-"}</div>
                    <div className="text-muted-foreground">{s.parent_phone} {s.parent_email && `· ${s.parent_email}`}</div>
                  </td>
                )}
                <td className="p-3">{s.grade_level}</td>
                <td className="p-3">{s.province} / {s.district}</td>
                <td className="p-3">{s.exam_type}</td>
                <td className="p-3 font-bold">%{s.overall_average}</td>
                <td className="p-3">{s.exams_completed}</td>
                <td className="p-3">
                  <Button size="sm" variant="outline" onClick={() => invite(s.child_id, s.parent_id)} data-testid={`tgt-invite-${s.child_id}`}>
                    <Mail className="h-3.5 w-3.5" /> Davet Gönder
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
